import { getLogger } from '../../../libs/logger';
import { StorageKeys } from '../../../shared/constants/storageKeys';

const logger = getLogger('chunkReload');

/**
 * Matches the dynamic-import / code-split failure messages emitted by both
 * bundlers this app has shipped:
 * - webpack: `ChunkLoadError` / "Loading chunk <id> failed"
 * - Vite / Rolldown: "Failed to fetch dynamically imported module" (Chromium),
 *   "Importing a module script failed" (Safari), "error loading dynamically
 *   imported module" (Firefox), and "Unable to preload CSS".
 *
 * The Vite variants are plain `TypeError`s (no `ChunkLoadError` name), so they
 * must be matched by message.
 */
const CHUNK_LOAD_ERROR_MESSAGE =
  /loading chunk [^ ]+ failed|failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|unable to preload css/i;

/**
 * Whether an error is a failed dynamic-import / code-split chunk load.
 */
export const isChunkLoadError = (error: unknown): boolean =>
  error instanceof Error &&
  (error.name === 'ChunkLoadError' ||
    CHUNK_LOAD_ERROR_MESSAGE.test(error.message));

/**
 * Query param appended to the URL when we trigger a recovery reload. Because it
 * survives the full-page navigation it acts as the durable one-shot guard:
 * unlike `sessionStorage`, it cannot be wiped mid-loop. The logout / 401 flow
 * clears `sessionStorage` on every page load (see `clearStorage` in
 * `authService`), so a stale chunk on `/logout` would otherwise reset the
 * storage guard and reload forever (~1×/sec).
 */
const CHUNK_RELOAD_PARAM = '_chunkReload';

/**
 * Whether the current URL already carries the recovery marker, i.e. this load is
 * the result of a reload we triggered. Storage-independent, so it holds even
 * when the visited route clears `sessionStorage`.
 */
const hasChunkReloadUrlMarker = (): boolean => {
  try {
    return new URL(window.location.href).searchParams.has(CHUNK_RELOAD_PARAM);
  } catch {
    return false;
  }
};

/**
 * Whether a recovery reload has already been attempted for this navigation.
 * Checks the URL marker first (durable across storage clears) and falls back to
 * the `sessionStorage` flag for cases where the marker is stripped.
 */
export const hasAttemptedChunkReload = (): boolean => {
  if (hasChunkReloadUrlMarker()) {
    return true;
  }
  try {
    return window.sessionStorage.getItem(StorageKeys.ChunkReloaded) !== null;
  } catch {
    return false;
  }
};

/**
 * Optional predicate reporting whether the user is currently in a live call.
 * When it returns `true`, {@link attemptChunkReloadOnce} skips the full-page
 * reload so a recoverable stale-chunk failure does not tear down an active
 * consultation; the retry + error-boundary fallback chain handles it instead.
 * Wired at startup via {@link setChunkReloadActiveCallGuard}; defaults to "not
 * in a call" so reload recovery stays enabled if the guard is never set.
 */
let isActiveCallGuard: (() => boolean) | undefined;

/**
 * Registers the predicate used to suppress chunk-reload recovery during a live
 * call. The latest guard wins. Kept injectable so this module stays decoupled
 * from the call feature and unit-testable in isolation.
 */
export const setChunkReloadActiveCallGuard = (guard: () => boolean): void => {
  isActiveCallGuard = guard;
};

/**
 * Throttles the active-call deferral branch. A chunk failure during a call can
 * recur across retries/remounts; report the deferral at most once per page load.
 */
let hasLoggedActiveCallDeferral = false;

/**
 * Whether a reload should be skipped because the user is in a live call.
 * Guard invocation is defensive: a throwing predicate must not block recovery.
 */
const shouldDeferReloadForActiveCall = (): boolean => {
  try {
    return isActiveCallGuard?.() === true;
  } catch {
    return false;
  }
};

/**
 * Throttles the unrecoverable branch. Once a page has reloaded and the chunk
 * still fails, a render/remount loop can call {@link attemptChunkReloadOnce}
 * thousands of times in a single session; log the unrecoverable state at most
 * once per page load.
 */
let hasLoggedUnrecoverableChunkError = false;

/**
 * Attempts a single full-page reload to recover from a stale-chunk failure
 * (typically a deploy rotated chunk hashes while the tab was open). The
 * one-shot guard ({@link hasAttemptedChunkReload}) prevents a reload loop —
 * primarily via the `_chunkReload` URL marker, which survives routes that wipe
 * `sessionStorage` (e.g. the logout / 401 flow).
 *
 * Skips the reload entirely while the user is in a live call (see
 * {@link setChunkReloadActiveCallGuard}) so a recoverable failure does not tear
 * down the consultation; the retry + boundary-fallback chain handles it then.
 *
 * @returns `true` if a reload was triggered; `false` if we deferred for an
 * active call or already reloaded once (so callers can render a fallback or let
 * the failure propagate instead of looping).
 */
export const attemptChunkReloadOnce = (error: unknown): boolean => {
  if (shouldDeferReloadForActiveCall()) {
    if (!hasLoggedActiveCallDeferral) {
      hasLoggedActiveCallDeferral = true;
      logger.warn(
        'chunk load failed during active call; deferring reload to avoid interrupting the call',
        error,
      );
    }
    return false;
  }
  if (hasAttemptedChunkReload()) {
    if (!hasLoggedUnrecoverableChunkError) {
      hasLoggedUnrecoverableChunkError = true;
      logger.error('chunk load failed; reload did not help', error);
    }
    return false;
  }
  logger.error('chunk load failed; attempting recovery', error);
  // Best-effort: the URL marker below is the authoritative loop guard, so a
  // throwing/full storage (e.g. Safari private mode) must not abort recovery.
  try {
    window.sessionStorage.setItem(StorageKeys.ChunkReloaded, '1');
  } catch {
    // ignore — the URL marker still prevents a reload loop
  }
  const url = new URL(window.location.href);
  url.searchParams.set(CHUNK_RELOAD_PARAM, Date.now().toString());
  window.location.replace(url.toString());
  return true;
};

let isPreloadErrorHandlerRegistered = false;

/**
 * Vite / Rolldown dispatches a cancelable `vite:preloadError` event on `window`
 * when a dynamically-imported module (or one of its preloaded dependencies)
 * fails to load — typically because a deploy rotated the hashed asset filenames
 * while the tab was open. Recover with the same one-time reload the error
 * boundaries use, before the rejection bubbles up as an uncaught error.
 *
 * Idempotent: safe to call once at app startup.
 */
export const registerChunkPreloadErrorHandler = (): void => {
  if (isPreloadErrorHandlerRegistered || typeof window === 'undefined') {
    return;
  }
  isPreloadErrorHandlerRegistered = true;

  window.addEventListener('vite:preloadError', (event) => {
    const preloadEvent = event as Event & { payload?: unknown };
    const reloaded = attemptChunkReloadOnce(preloadEvent.payload ?? event);
    // When we trigger a reload, cancel Vite's default rethrow so the failure
    // does not also surface to an error boundary. If we have already reloaded
    // once, let it propagate so the boundary can render its fallback rather
    // than the tab silently looping.
    if (reloaded) {
      event.preventDefault();
    }
  });
};
